import cn from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Loader, Modal } from "ui";

import { AiLesson } from "../../aiAgents.types";
import { useLessonFirePreview } from "../../apis";
import {
  AI_LESSON_FIRE_PREVIEW_DAYS,
  AI_LESSON_FIRE_PREVIEW_DEFAULT_DAYS
} from "../../constants/lessons";
import {
  isLessonTag,
  lessonConversationLink,
  parseLessonApiError
} from "../../helpers/lessons";

import styles from "./LessonFirePreviewModal.module.scss";

interface Props {
  assistantId: string;
  // Open when set.
  lesson: AiLesson | null;
  onClose: () => void;
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const Preview = ({
  assistantId,
  lesson,
  onClose
}: Props & { lesson: AiLesson }) => {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(AI_LESSON_FIRE_PREVIEW_DEFAULT_DAYS);
  const { preview, isLoading, error } = useLessonFirePreview(
    assistantId,
    lesson.id,
    days
  );

  const errorDetails = error ? parseLessonApiError(error) : null;

  return (
    <Modal
      isOpen
      title="aiLesson.preview.title"
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <Button type="button" variant="default" block onClick={onClose}>
            {t("aiLesson.close")}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <div className={styles.lessonTitle}>{lesson.title}</div>
        <div className={styles.muted}>{t("aiLesson.preview.free")}</div>

        <select
          className={styles.select}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label={t("aiLesson.preview.period", { days })}
        >
          {AI_LESSON_FIRE_PREVIEW_DAYS.map((option) => (
            <option key={option} value={option}>
              {t("aiLesson.preview.period", { days: option })}
            </option>
          ))}
        </select>

        {isLoading ? (
          <div className={styles.loaderWrap}>
            <Loader />
          </div>
        ) : errorDetails ? (
          <div className={styles.notice}>
            {errorDetails.endpointMissing
              ? t("aiLesson.unavailable")
              : t("aiLesson.preview.loadFailed")}
          </div>
        ) : preview ? (
          <>
            <div className={styles.result}>
              {t("aiLesson.preview.result", {
                matched: preview.matchedTurns.toLocaleString(),
                scanned: preview.scannedTurns.toLocaleString()
              })}
            </div>

            {preview.tooBroad ? (
              <div className={styles.warning}>
                {t("aiLesson.preview.tooBroad")}
              </div>
            ) : null}

            {preview.isUpperBound ? (
              <div className={styles.notice}>
                <div>{t("aiLesson.preview.upperBound")}</div>
                {preview.assumedTags.length > 0 ? (
                  <div className={styles.chipsRow}>
                    <span className={styles.chipsLabel}>
                      {t("aiLesson.preview.assumedTags")}
                    </span>
                    {preview.assumedTags.map((tag) => (
                      <span key={tag} className={styles.chip}>
                        {isLessonTag(tag) ? t(`aiLesson.tag.${tag}`) : tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {preview.truncated ? (
              <div className={styles.muted}>
                {t("aiLesson.preview.truncated", {
                  count: preview.scannedTurns
                })}
              </div>
            ) : null}

            {preview.samples.length === 0 ? (
              <div className={styles.empty}>{t("aiLesson.preview.empty")}</div>
            ) : (
              <div className={styles.samples}>
                <div className={styles.samplesTitle}>
                  {t("aiLesson.preview.samples")}
                </div>
                <ul className={styles.sampleList}>
                  {preview.samples.map((sample) => (
                    <li key={sample.turnMessageId} className={styles.sample}>
                      <div className={styles.sampleMeta}>
                        <span>{formatDate(sample.createdAt)}</span>
                        {/* A new tab keeps this list open. */}
                        <Link
                          className={styles.sampleLink}
                          to={lessonConversationLink(sample.conversationId)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("aiLesson.preview.open")}
                        </Link>
                      </div>
                      <div
                        className={cn(styles.sampleText, {
                          [styles.sampleTextEmpty]: !sample.inboundText
                        })}
                      >
                        {sample.inboundText || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Modal>
  );
};

// "Where would it apply?": the turns of the last days this lesson would have
// matched, from the stored messages only. Free (no AI call) and read-only,
// so it does not need the permission.
const LessonFirePreviewModal = ({ lesson, ...props }: Props) =>
  lesson ? <Preview key={lesson.id} lesson={lesson} {...props} /> : null;

export default LessonFirePreviewModal;
